import { io } from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.isConnected = false;
  }

  connect(userId) {
    if (this.socket?.connected) {
      return;
    }

    // Use the same domain as your website (no wss://, use https://)
    const WS_URL = process.env.REACT_APP_WS_URL || window.location.origin;
    
    // Get JWT token from localStorage
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      console.error('No authentication token found for Socket.IO');
      return;
    }
    
    console.log('Connecting to Socket.IO at:', WS_URL);
    
    this.socket = io(WS_URL, {
      path: '/socket.io',
      query: { 
        userId: userId,
        token: token
      },
      // Use ONLY HTTP polling - no WebSocket
      transports: ['polling'],
      // Don't upgrade to WebSocket
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      withCredentials: true,
      auth: {
        token: token
      }
    });

    this.socket.on('connect', () => {
      console.log('Socket.IO connected via HTTP polling');
      this.isConnected = true;
      // Subscribe to burner room on connection
      this.subscribeToBurner();
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error.message);
      this.isConnected = false;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
      this.isConnected = false;
    });

    this.socket.on('error', (error) => {
      console.error('Socket.IO error:', error);
    });

    // Handle incoming messages
    this.socket.onAny((event, data) => {
      console.log('Socket.IO received event:', event, data);
      const callbacks = this.listeners.get(event) || [];
      callbacks.forEach(callback => callback(data));
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      this.isConnected = false;
      console.log('Socket.IO disconnected');
    }
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event).filter(cb => cb !== callback);
    if (callbacks.length === 0) {
      this.listeners.delete(event);
    } else {
      this.listeners.set(event, callbacks);
    }
  }

  emit(event, data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit(event, data);
      console.log('Socket.IO emitted:', event, data);
    } else {
      console.warn('Socket.IO not connected, cannot emit:', event);
    }
  }

  joinAuction(auctionId) {
    this.emit('join_auction', { auction_id: auctionId });
  }

  leaveAuction(auctionId) {
    this.emit('leave_auction', { auction_id: auctionId });
  }

  subscribeToBurner() {
    if (this.socket && this.socket.connected) {
      this.socket.emit('subscribe_burner');
      console.log('Subscribed to burner room');
    }
  }

  unsubscribeFromBurner() {
    if (this.socket && this.socket.connected) {
      this.socket.emit('unsubscribe_burner');
      console.log('Unsubscribed from burner room');
    }
  }

  onBurnerMessage(callback) {
    this.on('new_burner_message', callback);
  }

  offBurnerMessage(callback) {
    this.off('new_burner_message', callback);
  }
}

export const websocketService = new WebSocketService();